import { createContext, redirect, type MiddlewareFunction } from 'react-router';
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import type { Role, SessionUserDto } from '@ceo/shared';
import { ApiError, api } from '@/lib/api';
import { queryClient } from '@/lib/queries';

export const meQuery = queryOptions({
  queryKey: ['admin', 'me'],
  queryFn: async () => {
    try {
      return await api<SessionUserDto>('/auth/me');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },
  staleTime: 5 * 60_000,
});

export const adminUserContext = createContext<SessionUserDto>();

/** React Router middleware guarding every /admin route except the login page. */
export const requireAdmin: MiddlewareFunction = async ({ request, context }) => {
  const user = await queryClient.ensureQueryData(meQuery);
  if (!user) {
    const { pathname, search } = new URL(request.url);
    throw redirect(`/admin/login?next=${encodeURIComponent(pathname + search)}`);
  }
  const { pathname } = new URL(request.url);
  // Until the password is changed / two-step verification is on, only the account page opens.
  if ((user.mustChangePassword || user.mustSetUpTwoFactor) && pathname !== '/admin/account') {
    throw redirect('/admin/account');
  }
  context.set(adminUserContext, user);
};

/** Extra guard for routes only some roles may open (the API enforces the same rule). */
export const requireRole =
  (...roles: Role[]): MiddlewareFunction =>
  ({ context }) => {
    if (!roles.includes(context.get(adminUserContext).role)) throw redirect('/admin');
  };

export function useMe() {
  return useSuspenseQuery(meQuery).data!;
}

export async function logout() {
  await api('/auth/logout', { method: 'POST' });
  queryClient.removeQueries({ queryKey: ['admin'] });
  queryClient.setQueryData(meQuery.queryKey, null);
}
